export interface OpeningInterval {
  start: string;
  end: string;
}

export type OpeningHours = Partial<
  Record<
    'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
    OpeningInterval[]
  >
>;

export interface StorefrontOpeningStatus {
  isOpen: boolean;
  label: 'Ouvert' | 'Fermé';
}

const WEEKDAYS: Array<keyof OpeningHours> = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

function minutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour <= 23 && minute <= 59 ? hour * 60 + minute : null;
}

export function storefrontOpeningStatus(
  hours: OpeningHours | null | undefined,
  timezone: string | null | undefined,
  now: Date = new Date(),
): StorefrontOpeningStatus | null {
  if (!hours || Object.keys(hours).length === 0) return null;

  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || 'UTC',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
  } catch {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
  }

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value;
  const weekday = part('weekday')?.toLowerCase() as keyof OpeningHours | undefined;
  const current = Number(part('hour')) * 60 + Number(part('minute'));
  const intervals = weekday ? (hours[weekday] ?? []) : [];
  const opensToday = intervals.some((interval) => {
    const start = minutes(interval.start);
    const end = minutes(interval.end);
    if (start === null || end === null || start === end) return false;
    return start < end ? current >= start && current < end : current >= start;
  });
  const weekdayIndex = weekday ? WEEKDAYS.indexOf(weekday) : -1;
  const previousDay = weekdayIndex >= 0 ? WEEKDAYS[(weekdayIndex + 6) % 7] : undefined;
  const continuesFromYesterday = (previousDay ? (hours[previousDay] ?? []) : []).some(
    (interval) => {
      const start = minutes(interval.start);
      const end = minutes(interval.end);
      return start !== null && end !== null && start > end && current < end;
    },
  );
  const isOpen = opensToday || continuesFromYesterday;

  return { isOpen, label: isOpen ? 'Ouvert' : 'Fermé' };
}
